import React from 'react'
import { matchPath, useLocation } from 'react-router'
import styled from '@emotion/styled'
import { hexColors } from '@mobi/settings'
import { Icon } from '@mobi/component-library/Assets/Icon'
import { radius } from '@mobi/component-library/Foundations'
import { SpinnerInlineStyled } from '@mobi/component-library/Legacy/Spinner/Spinner.styles'
import { isReactNativeApp } from '@mobi/web-native-comms/web'
import { AppRoutes } from '@core/AppRoutes'
import { shouldApplySafeAreaBottomPadding } from '@core/Settings/cssConditions'
import { selectIsSkyVideoPlayerOpen } from '@core/Areas/SkyVideoPlayer/Store/selectors'
import { useAppSelector } from '@core/Store/hooks'
import { selectIsRadioBusy, selectIsRadioPlaying } from '../../Store/selectors'
import { TabRadioIcon } from './Components/TabRadioIcon'
import { trackOnRadioToggle } from './helpers/analytics'
import { useWebRadio } from './hooks/useWebRadio'
import { useNativeRadio } from './hooks/useNativeRadio'

export const FloatingTabRadioButton: React.FC = () => {
  const isRadioPlaying = useAppSelector(selectIsRadioPlaying)
  const isRadioBusy = useAppSelector(selectIsRadioBusy)
  const isSkyVideoPlayerOpen = useAppSelector(selectIsSkyVideoPlayerOpen)

  const nativeRadio = useNativeRadio({ isEnabled: isReactNativeApp, isRadioPlaying })
  const webRadio = useWebRadio({ isEnabled: !isReactNativeApp, isRadioPlaying })

  const handlePause = isReactNativeApp ? nativeRadio.pause : webRadio.pause

  React.useEffect(() => {
    if (isSkyVideoPlayerOpen) handlePause()
  }, [isSkyVideoPlayerOpen, handlePause])

  const location = useLocation()
  const isVisible = matchPath(AppRoutes.Home + '/*', location.pathname)

  const handleRadioPlayToggle = () => {
    isReactNativeApp ? nativeRadio.toggle() : webRadio.toggle()
    trackOnRadioToggle(isRadioPlaying, 'floating-nav', `${location.pathname}${location.search}`)
  }

  return (
    <FloatingTabRadioButtonContainerStyled
      id='floatingRadioPlayer'
      style={{ display: isVisible ? undefined : 'none' }}
    >
      <button
        role='button'
        type='button'
        name='tab-radio-button'
        aria-label='TAB Radio Button'
        onClick={handleRadioPlayToggle}
      >
        <TabRadioIcon />
      </button>

      {(isRadioPlaying || isRadioBusy) && (
        <div data-testid='tab-radio-playing'>
          {isRadioBusy ? (
            <SpinnerInlineStyled size={1.2} />
          ) : (
            <Icon name='SolidPlayCircle' size='1.7rem' color='white' />
          )}
        </div>
      )}
    </FloatingTabRadioButtonContainerStyled>
  )
}

// ======
// Styles
// ======

const FloatingTabRadioButtonContainerStyled = styled.div({
  width: '5rem',
  height: '5rem',
  background: 'linear-gradient(180deg, #8246AF 24.48%, #6A3A8F 48.96%, #512D6D 74.48%);',
  borderRadius: '1rem',
  zIndex: '3',
  position: 'fixed',
  display: 'flex',
  right: 'calc(max(1rem, 50vw - 40rem + 1rem))',
  filter: 'drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.25))',
  alignItems: 'center',
  bottom: shouldApplySafeAreaBottomPadding
    ? 'calc(7rem + var(--safe-area-inset-bottom, 0px))'
    : '2rem',

  button: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    border: 'none',
    backgroundColor: 'transparent',
    padding: 0,
    color: hexColors.white,
    width: '100%',
    fontSize: '3em',
    cursor: 'pointer',
  },

  '> div': {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: '1.8rem',
    height: '1.8rem',
    position: 'absolute',
    right: '0.2rem',
    top: '0.2rem',
    padding: '0.5px',
    color: hexColors.white,
    background: '#9E56FF',
    borderRadius: radius.full,
    pointerEvents: 'none',
  },
})
